"use client";

import { ArrowDown, Check, Layers } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { SectionHeading } from "../../home/section";
import { Stagger, StaggerItem } from "../../home/motion";

/* ------------------------------------------------------------------ */
/* Shared mock chrome                                                 */
/* ------------------------------------------------------------------ */

function MiniCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-xl border border-border bg-background px-3 py-2.5 shadow-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex justify-center">
      <ArrowDown className="size-3.5 text-muted-foreground" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1 · Normalization — every system mapped to one model               */
/* ------------------------------------------------------------------ */

function RawRow({ img, name, raw }: { img: string; name: string; raw: string }) {
  return (
    <MiniCard className="py-2">
      <Image
        src={img}
        alt={name}
        width={18}
        height={18}
        className="size-[18px] shrink-0 rounded object-contain"
      />
      <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground">
        {name}
      </span>
      <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
        {raw}
      </code>
    </MiniCard>
  );
}

function NormalizeMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      <RawRow img="/images/integrations/mariana-tek/icon.png" name="Mariana Tek" raw="no_show" />
      <RawRow img="/images/integrations/fitdegree/icon.png" name="fitDEGREE" raw="absent" />
      <Arrow />
      <div className="flex items-center justify-center gap-1.5 rounded-xl border border-[hsl(var(--brand-accent)/0.35)] bg-[hsl(var(--brand-accent)/0.08)] px-3 py-2 shadow-sm">
        <Layers className="size-3.5 text-[hsl(var(--brand-accent))]" />
        <span className="text-[12px] font-semibold text-foreground">No-show</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Real-time — events update the profile the moment they happen   */
/* ------------------------------------------------------------------ */

function RealTimeMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      <MiniCard className="py-2">
        <span className="relative flex size-2 shrink-0">
          <span className="absolute inline-flex size-full rounded-full bg-success/60" />
          <span className="relative inline-flex size-2 rounded-full bg-success" />
        </span>
        <span className="min-w-0 flex-1 truncate text-[12px] text-foreground">
          <span className="font-semibold">Checked in</span> · 6:00 PM HIIT
        </span>
        <span className="shrink-0 text-[10px] text-muted-foreground">now</span>
      </MiniCard>
      <Arrow />
      <MiniCard className="justify-between py-2.5">
        <span className="text-[12px] text-muted-foreground">Last visit</span>
        <span className="text-[12px] font-semibold text-foreground">Just now</span>
      </MiniCard>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Attribution — ad source carried through to the membership      */
/* ------------------------------------------------------------------ */

function AttributionMock() {
  return (
    <div className="w-full max-w-[244px] space-y-2">
      <MiniCard className="py-2">
        <Image
          src="/images/integrations/facebook/icon.png"
          alt="Facebook"
          width={18}
          height={18}
          className="size-[18px] shrink-0 rounded-sm object-contain"
        />
        <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground">
          Facebook ads
        </span>
        <span className="shrink-0 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
          New lead
        </span>
      </MiniCard>
      <Arrow />
      <MiniCard className="py-2">
        <Avatar name="Maya Chen" size="xs" />
        <span className="min-w-0 flex-1 truncate text-[12px] text-foreground">
          <span className="font-semibold">Maya</span> joined
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[hsl(var(--brand-accent)/0.35)] bg-[hsl(var(--brand-accent)/0.08)] px-2 py-0.5 text-[10px] font-medium text-foreground">
          <Check className="size-2.5 text-[hsl(var(--brand-accent))]" strokeWidth={3} />
          spring-promo
        </span>
      </MiniCard>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

type Highlight = { visual: ReactNode; title: string; body: string };

const HIGHLIGHTS: Highlight[] = [
  {
    visual: <NormalizeMock />,
    title: "One model, every system",
    body: "Mariana Tek, Mindbody, and fitDEGREE each speak their own language. Helios maps them to one set of fields, so a segment means the same thing everywhere.",
  },
  {
    visual: <RealTimeMock />,
    title: "Always current, never nightly",
    body: "The moment someone books, checks in, or lapses, their profile updates, so you act on what's true now instead of last night's export.",
  },
  {
    visual: <AttributionMock />,
    title: "From ad click to paying member",
    body: "Leads arrive tagged with the campaign that drove them, and Helios follows them to the sale, so you see which ads produce members, not just clicks.",
  },
];

export function CustomerDataHighlights() {
  return (
    <section className="bg-background pt-20 lg:pt-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="left"
          size="sm"
          title={
            <>
              Your booking system runs the schedule. It was never built to run your marketing.
              <span className="text-muted-foreground">
                {" "}
                Helios turns every source into one cohesive profile, ready to segment,
                message, and act on.
              </span>
            </>
          }
        />
      </div>

      {/* Framed band: hatched side gutters, solid card body (Attio-style). */}
      <div className="mt-12 border-t border-border bg-grid lg:mt-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Stagger className="grid grid-cols-1 divide-y divide-border border-x border-border bg-background lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {HIGHLIGHTS.map((h) => (
              <StaggerItem key={h.title} className="flex flex-col px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex h-[188px] items-center justify-center">{h.visual}</div>
                <div className="mt-7">
                  <h3 className="text-base font-bold tracking-tight text-foreground">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
